import { createContext, useContext, type ReactNode } from 'react'
import { useTerminology } from '@/hooks/useTerminology'
import { DEFAULT_LABELS, type AppLabels } from '@/types'

const TerminologyContext = createContext<AppLabels>(DEFAULT_LABELS)

interface TerminologyProviderProps {
  children: ReactNode
}

export function TerminologyProvider({ children }: TerminologyProviderProps) {
  const { data: labels } = useTerminology()

  return (
    <TerminologyContext.Provider value={labels ?? DEFAULT_LABELS}>
      {children}
    </TerminologyContext.Provider>
  )
}

/**
 * Returns the resolved app labels (custom or defaults).
 * Must be used within a TerminologyProvider.
 */
export function useLabels(): AppLabels {
  return useContext(TerminologyContext)
}

/**
 * Strips trailing 's' to produce a singular form.
 * "Podcasts" -> "Podcast", "Episodes" -> "Episode", "Guests" -> "Guest"
 */
export function singular(label: string): string {
  if (label.endsWith('ies')) {
    // "Series" stays "Series" (already singular-looking)
    if (label === 'Series') return label
    // e.g., not expected but handle gracefully
    return label
  }
  if (label.endsWith('s') && !label.endsWith('ss')) {
    return label.slice(0, -1)
  }
  return label
}
